import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "@components/cards/Card/Card";
import TextInput from "@components/inputs/TextInput/TextInput";
import styles from "@styles/CommonEditorPageLayout.module.css";
import MediaSelector from "@components/inputs/MediaSelector/MediaSelector";
import DateInput from "@components/inputs/DateInput/DateInput";
import TimeInput, { TimeValue } from "@components/inputs/TimeInput/TimeInput";
import LocationInput from "@components/inputs/LocationInput/LocationInput";
import { isRequiredField, isValidMaxLength, isValidUrl } from "@util/validators";
import CheckboxWithLabel from "@components/inputs/Checkbox/CheckboxWithLabel";
import SelectInput from "@components/inputs/SelectInput/SelectInput";
import UnsavedChangesPrompt from "@components/misc/UnsavedChangesPrompt";
import EditorCompleteButtons from "@components/buttons/EditorCompleteButtons/EditorCompleteButtons";
import FileSelector from "@components/inputs/FileSelector/FileSelector";
import {
    MAX_MEMORIAL_OBITUARY_LENGTH,
    MAX_MEMORIAL_SERVICE_BOOK_FILE_SIZE,
    MAX_MEMORIAL_LOCATION_LENGTH,
    MAX_MEMORIAL_SERVICE_LOCATION_LENGTH,
    MAX_MEMORIAL_SERVICE_INFORMATION_LENGTH,
    MAX_MEMORIAL_SERVICE_NAME_LENGTH,
    MAX_MEMORIAL_SERVICE_PLOT_INFO_LENGTH,
} from "@constants/validation";
import { MediaFile } from "@models/Common";
import { createMemorial, fetchMemorial, updateMemorialInfo, upgradeMemorial } from "@services/memorialService";
import ConfirmationPopup from "@components/modals/ConfirmationPopup/ConfirmationPopup";
import { Memorial, MemorialInfoUpdate } from "@models/Memorial";
import { ServiceMaterialType } from "@models/ServiceMaterial";
import { Charity } from "@models/Charity";
import { fetchCharities } from "@services/charityService";
import { fetchBusiness } from "@services/businessService";
import { useAuth } from "@context/auth-context";
import { delay } from "@util/time";
import Button from "@components/buttons/Button/Button";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { Privacy } from "@models/Privacy";
import { AxiosProgressEvent } from "axios";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Snackbar from "@components/display/SnackBar/SnackBar";
import { getRootDomain } from "@util/domain";
import { trackEvent } from "@util/ga";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

type ServiceMaterialOption = {
    value: ServiceMaterialType;
    label: string;
};

const SERVICE_MATERIAL_OPTIONS: ServiceMaterialOption[] = [
    // {
    //     value: "service book",
    //     label: "Service Book",
    // },
    {
        value: "oos",
        label: "Order of Service",
    },
    {
        value: "mass book",
        label: "Mass Book",
    },
    {
        value: "memorial card",
        label: "Memorial Card",
    },
    {
        value: "bookmark",
        label: "Bookmark",
    },
];

const DEFAULT_PRIVACY: Privacy = {
    published: true,
    noIndex: false,
    guestBookOpen: false,
    tributesOpen: true,
    emailOnTribute: false,
    restrictPostContentToQr: false,
    restrictViewInfoToQr: false,
    restrictViewTimelineGalleryToQr: false,
    restrictViewTributesToQr: false,
};

const MemorialEditorPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mode = searchParams.get("mode");
    const id = searchParams.get("id");
    const type = searchParams.get("type"); // "pet" for pet memorials

    const { authState, refreshUser } = useAuth();
    const { user } = authState;

    const [loading, setLoading] = useState(mode === "edit");
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [changedInfoFields, setChangedInfoFields] = useState<MemorialInfoUpdate>({});
    const [iframeKey, setIframeKey] = useState(Date.now());
    const [qrKey, setQrKey] = useState("");

    const [charities, setCharities] = useState<Charity[]>([]);
    const [image, setImage] = useState<MediaFile | null>(null);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [birthDate, setBirthDate] = useState<Date | null>(null);
    const [deathDate, setDeathDate] = useState<Date | null>(null);
    const [birthYear, setBirthYear] = useState<string>("");
    const [deathYear, setDeathYear] = useState<string>("");
    const [birthYearOnly, setBirthYearOnly] = useState(false);
    const [deathYearOnly, setDeathYearOnly] = useState(false);
    const [birthplace, setBirthplace] = useState("");
    const [residence, setResidence] = useState("");
    const [serviceDateTime, setServiceDateTime] = useState<Date | null>(null);
    const [serviceLocation, setServiceLocation] = useState("");
    const [serviceInformation, setServiceInformation] = useState("");
    const [graveLocation, setGraveLocation] = useState("");
    const [plotInfo, setPlotInfo] = useState("");
    const [serviceMaterial, setServiceMaterial] = useState<MediaFile | null>(null);
    const [serviceMaterialType, setServiceMaterialType] = useState<ServiceMaterialType | null>(null);
    const [liveStreamLink, setLivestreamLink] = useState("");
    const [charityId, setCharityId] = useState("");
    const [fundraiserUrl, setFundraiserUrl] = useState("");
    const [pageAdminEmail, setMemorialAdminEmail] = useState("");
    const [privacy, setPrivacy] = useState<Privacy>({ ...DEFAULT_PRIVACY });
    const [obituary, setObituary] = useState("");
    const [serviceId, setServiceId] = useState<string | undefined>("");
    const [businessId, setBusinessId] = useState<string | undefined>("");
    const [hideSendFlowers, setHideSendFlowers] = useState(false);
    const [businessHideSendFlowers, setBusinessHideSendFlowers] = useState(false);
    const [isFreeVersion, setIsFreeVersion] = useState(user?.userType === "standard_user");
    const [isPetMemorial, setIsPetMemorial] = useState(type === "pet");
    const [showUpgradePopup, setShowUpgradePopup] = useState(false);
    const [memorial, setMemorial] = useState<Memorial | null>(null);

    const [imageError, setImageError] = useState("");
    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [birthDateError, setBirthDateError] = useState("");
    const [deathDateError, setDeathDateError] = useState("");
    const [birthYearError, setBirthYearError] = useState("");
    const [deathYearError, setDeathYearError] = useState("");
    const [birthplaceError, setBirthplaceError] = useState("");
    const [residenceError, setResidenceError] = useState("");
    const [serviceLocationError, setServiceLocationError] = useState("");
    const [serviceInformationError, setServiceInformationError] = useState("");
    const [serviceMaterialError, setServiceMaterialError] = useState("");
    const [serviceMaterialTypeError, setServiceMaterialTypeError] = useState("");
    const [liveStreamLinkError, setLiveStreamLinkError] = useState("");
    const [fundraiserUrlError, setFundraiserUrlError] = useState("");
    const [graveLocationError, setGraveLocationError] = useState("");
    const [plotInfoError, setPlotInfoError] = useState("");
    const [obituaryError, setObituaryError] = useState("");
    const [submitErrorMessage, setSubmitErrorMessage] = useState("");

    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error" | "info" | "warning";
    }>({
        open: false,
        message: "",
        severity: "success",
    });

    const hasUnsavedChanges = Object.keys(changedInfoFields).length > 0;

    const guestBookAllowed = !isPetMemorial && serviceId;

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const fetchedCharities = await fetchCharities();
                setCharities(fetchedCharities);

                if (mode === "edit") {
                    if (!id) {
                        throw new Error("No ID.");
                    }
                    const data = await fetchMemorial(id);
                    setMemorial(data);
                    setFirstName(data.firstName);
                    setLastName(data.lastName);
                    setBirthDate(data.birthDate || null);
                    setDeathDate(data.deathDate || null);
                    setBirthYear(data.birthYear ? String(data.birthYear) : "");
                    setDeathYear(data.deathYear ? String(data.deathYear) : "");
                    setBirthplace(data.birthplace || "");
                    setResidence(data.residence || "");
                    setServiceDateTime(data.serviceDateTime
                        ? new Date(data.serviceDateTime) : null);
                    setServiceLocation(data.serviceLocation || "");
                    setServiceInformation(data.serviceInformation || "");
                    setServiceMaterial(data.serviceMaterial?.file || null);
                    setServiceMaterialType(data.serviceMaterial?.type || null);
                    setLivestreamLink(data.liveStreamLink || "");
                    setGraveLocation(data.graveLocation || "");
                    setPlotInfo(data.plotInfo || "");
                    setCharityId(data.fundraiserUrl ? "custom" : data.charityId || "");
                    setFundraiserUrl(data.fundraiserUrl || "");
                    setMemorialAdminEmail(data.pageAdminEmail || "");
                    setPrivacy(data.privacy);
                    setObituary(data.obituary || "");
                    setImage(data.image || null);
                    setServiceId(data.serviceId);
                    setBusinessId(data.businessId);
                    setHideSendFlowers(!!data.hideSendFlowers);
                    setBusinessHideSendFlowers(!!data.businessHideSendFlowers);
                    setIsFreeVersion(!!data.isFreeVersion);
                    setIsPetMemorial(!!data.isPetMemorial);
                    setQrKey(data.qrKey);

                    if (data.birthYear && !data.birthDate) {
                        setBirthYearOnly(true);
                    }

                    if (data.deathYear && !data.deathDate) {
                        setDeathYearOnly(true);
                    }

                    // Hide option to unpublish if already published
                    // if (data.privacy.published) {
                    //     setShowPublishButton(false);
                    // }
                } else {
                    // ENsure default privacy settings exist on create
                    setChangedInfoFields((prev) => {
                        const newState = { ...prev };
                        newState.privacy = { ...prev.privacy as Privacy };
                        newState.privacy = {
                            ...DEFAULT_PRIVACY,
                        };
                        return newState;
                    });

                    // New memorials belong to the user's business, so check its Send Flowers setting
                    if (user?.businessId) {
                        const business = await fetchBusiness(user.businessId).catch(() => null);
                        setBusinessHideSendFlowers(!!business?.hideSendFlowers);
                    }
                }
            } catch (err) {
                setError("Failed to fetch memorial information.");
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, [mode, id]);

    // Validation functions for fields
    const validateFirstName = (val: string) => {
        const result = isRequiredField(val, "First name");
        if (result) return result;
        return isValidMaxLength(val, MAX_MEMORIAL_SERVICE_NAME_LENGTH);
    };

    const validateLastName = (val: string) => {
        if (!isPetMemorial) {
            const result = isRequiredField(val, "Last name");
            if (result) return result;
        }
        return isValidMaxLength(val, MAX_MEMORIAL_SERVICE_NAME_LENGTH);
    };

    const validateBirthDeathDate = (val: Date | null) => {
        // return val ? "" : "Date of birth is required.";
        return "";
    };

    const validateBirthDeathYear = (val: string) => {
        if (val === "" || (Number(val) >= 1000 && Number(val) <= 9999)) {
            return "";
        }
        return "Invalid Year";
    };

    const validateLocation = (val: string) => {
        if (val) return isValidMaxLength(val, MAX_MEMORIAL_LOCATION_LENGTH);
        return "";
    };

    const validateServiceLocation = (val: string) => {
        if (val) return isValidMaxLength(val, MAX_MEMORIAL_SERVICE_LOCATION_LENGTH);
        return "";
    };

    const validateServiceInformation = (val: string) => {
        if (val) return isValidMaxLength(val, MAX_MEMORIAL_SERVICE_INFORMATION_LENGTH);
        return "";
    };

    const validateGraveLocation = (val: string) => {
        if (val) {
            return isValidMaxLength(val, MAX_MEMORIAL_SERVICE_LOCATION_LENGTH);
        }
        return "";
    };

    const validatePlotInfo = (val: string) => {
        if (val) {
            return isValidMaxLength(val, MAX_MEMORIAL_SERVICE_PLOT_INFO_LENGTH);
        }
        return "";
    };

    const validateServiceMaterialType = (val: ServiceMaterialType | null) => {
        return "";
    };

    const validateLivestreamLink = (val: string) => {
        if (val) {
            return isValidUrl(val);
        }
        return "";
    };

    const validateFundraiserUrl = (val: string) => {
        if (val) {
            return isValidUrl(val);
        }
        return "";
    };

    const validateObituary = (val: string) => {
        if (val) return isValidMaxLength(val, MAX_MEMORIAL_OBITUARY_LENGTH);
        return "";
    };

    // Input handlers with validation and setting changed fields
    const onFirstNameChange = useCallback((val: string) => {
        setFirstName(val);
        setFirstNameError(validateFirstName(val));
        setChangedInfoFields((prev) => ({ ...prev, firstName: val }));
    }, []);

    const onLastNameChange = useCallback((val: string) => {
        setLastName(val);
        setLastNameError(validateLastName(val));
        setChangedInfoFields((prev) => ({ ...prev, lastName: val }));
    }, [validateLastName]);

    const onBirthDateChange = useCallback((val: Date | null) => {
        setBirthDate(val);
        setBirthDateError(validateBirthDeathDate(val));
        setChangedInfoFields((prev) => ({ ...prev, birthDate: val || null }));
    }, []);

    const onDeathDateChange = useCallback((val: Date | null) => {
        setDeathDate(val);
        setDeathDateError(validateBirthDeathDate(val));
        setChangedInfoFields((prev) => ({ ...prev, deathDate: val || null }));
    }, []);

    const onBirthYearChange = useCallback((val: string) => {
        setBirthYear(val);
        setBirthYearError(validateBirthDeathYear(val));
        setChangedInfoFields((prev) => ({ ...prev, birthYear: Number(val) }));
    }, []);

    const onDeathYearChange = useCallback((val: string) => {
        setDeathYear(val);
        setDeathYearError(validateBirthDeathYear(val));
        setChangedInfoFields((prev) => ({ ...prev, deathYear: Number(val) }));
    }, []);

    const onBirthYearOnlyChange = useCallback((val: boolean) => {
        setBirthYearOnly(val);
        if (val) {
            setBirthDate(null);
            setBirthDateError("");
            setChangedInfoFields((prev) => ({ ...prev, birthDate: undefined }));
        } else {
            setBirthYear("");
            setBirthYearError("");
            setChangedInfoFields((prev) => ({ ...prev, birthYear: undefined }));
        }
    }, []);

    const onDeathYearOnlyChange = useCallback((val: boolean) => {
        setDeathYearOnly(val);
        if (val) {
            setDeathDate(null);
            setDeathDateError("");
            setChangedInfoFields((prev) => ({ ...prev, deathDate: undefined }));
        } else {
            setDeathYear("");
            setDeathYearError("");
            setChangedInfoFields((prev) => ({ ...prev, deathYear: undefined }));
        }
    }, []);

    const onServiceDateChange = useCallback((val: Date | null) => {
        if (val) {
            setServiceDateTime((prevServiceDateTime) => {
                if (prevServiceDateTime) {
                    const utcServiceDateTime = new Date(Date.UTC(
                        val.getFullYear(),
                        val.getMonth(),
                        val.getDate(),
                        prevServiceDateTime.getUTCHours(),
                        prevServiceDateTime.getUTCMinutes(),
                    ));
                    return utcServiceDateTime;
                }
                return val;
            });
        } else {
            setServiceDateTime(null);
        }
        setChangedInfoFields((prev) => {
            const newState = { ...prev };
            const prevDateTime = serviceDateTime;
            const newDate = prevDateTime && val
                ? new Date(
                    Date.UTC(
                        val.getUTCFullYear(),
                        val.getUTCMonth(),
                        val.getUTCDate(),
                        prevDateTime.getUTCHours(),
                        prevDateTime.getUTCMinutes(),
                    ),
                )
                : val || null;
            newState.serviceDateTime = newDate;
            return newState;
        });
    }, [serviceDateTime]);

    const onServiceTimeChange = useCallback((val: TimeValue | null) => {
        if (val) {
            setServiceDateTime((prevServiceDateTime) => {
                if (prevServiceDateTime) {
                    const newServiceDateTime = new Date(prevServiceDateTime);
                    newServiceDateTime.setUTCHours(Number(val.hours), Number(val.minutes), 0, 0);
                    return newServiceDateTime;
                }
                return null;
            });
        }
        setChangedInfoFields((prev) => {
            const newState = { ...prev };
            const prevDateTime = serviceDateTime;
            const newDate = prevDateTime ? new Date(prevDateTime) : new Date();

            if (val) {
                // Set hours and minutes in UTC
                newDate.setUTCHours(Number(val.hours), Number(val.minutes), 0, 0);
            }
            newState.serviceDateTime = newDate;
            return newState;
        });
    }, [serviceDateTime]);

    const onServiceLocationChange = useCallback((val: string) => {
        setServiceLocation(val);
        setServiceLocationError(validateServiceLocation(val));
        setChangedInfoFields((prev) => ({ ...prev, serviceLocation: val }));
    }, []);

    const onServiceInformationChange = useCallback((val: string) => {
        setServiceInformation(val);
        setServiceInformationError(validateServiceInformation(val));
        setChangedInfoFields((prev) => ({ ...prev, serviceInformation: val }));
    }, []);

    const onLiveStreamLinkChange = useCallback((val: string) => {
        setLivestreamLink(val);
        setLiveStreamLinkError(validateLivestreamLink(val));
        setChangedInfoFields((prev) => ({ ...prev, liveStreamLink: val }));
    }, []);

    const onFundraiserUrlChange = useCallback((val: string) => {
        setFundraiserUrl(val);
        setFundraiserUrlError(validateFundraiserUrl(val));
        setChangedInfoFields((prev) => ({ ...prev, fundraiserUrl: val }));
    }, []);

    const onGraveLocationChange = useCallback((val: string) => {
        setGraveLocation(val);
        setGraveLocationError(validateGraveLocation(val));
        setChangedInfoFields((prev) => ({ ...prev, graveLocation: val }));
    }, []);

    const onPlotInfoChange = useCallback((val: string) => {
        setPlotInfo(val);
        setPlotInfoError(validatePlotInfo(val));
        setChangedInfoFields((prev) => ({ ...prev, plotInfo: val }));
    }, []);

    const onBirthplaceChange = useCallback((val: string) => {
        setBirthplace(val);
        setBirthplaceError(validateLocation(val));
        setChangedInfoFields((prev) => ({ ...prev, birthplace: val }));
    }, []);

    const onResidenceChange = useCallback((val: string) => {
        setResidence(val);
        setResidenceError(validateLocation(val));
        setChangedInfoFields((prev) => ({ ...prev, residence: val }));
    }, []);

    const onObituaryChange = useCallback((val: string) => {
        setObituary(val);
        setObituaryError(validateObituary(val));
        setChangedInfoFields((prev) => ({ ...prev, obituary: val }));
    }, []);

    const progressCallback = useCallback((progressEvent: AxiosProgressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        setUploadProgress(progress);
    }, []);

    // Save handler
    const handleSave = async () => {
        let isValid = true;

        const firstNameValidationError = validateFirstName(firstName);
        setFirstNameError(firstNameValidationError);
        if (firstNameValidationError) isValid = false;

        const lastNameValidationError = validateLastName(lastName);
        setLastNameError(lastNameValidationError);
        if (lastNameValidationError) isValid = false;

        if (birthYearOnly) {
            const birthYearValidationError = validateBirthDeathYear(birthYear);
            setBirthYearError(birthYearValidationError);
            if (birthYearValidationError) isValid = false;
        } else {
            const birthDateValidationError = validateBirthDeathDate(birthDate);
            setBirthDateError(birthDateValidationError);
            if (birthDateValidationError) isValid = false;
        }

        if (deathYearOnly) {
            const deathYearValidationError = validateBirthDeathYear(deathYear);
            setDeathYearError(deathYearValidationError);
            if (deathYearValidationError) isValid = false;
        } else {
            const deathDateValidationError = validateBirthDeathDate(deathDate);
            setDeathDateError(deathDateValidationError);
            if (deathDateValidationError) isValid = false;
        }

        const birthplaceValidationError = validateLocation(birthplace);
        setBirthplaceError(birthplaceValidationError);
        if (birthplaceValidationError) isValid = false;

        const residenceValidationError = validateLocation(residence);
        setResidenceError(residenceValidationError);
        if (residenceValidationError) isValid = false;

        const serviceLocationValidationError = validateServiceLocation(serviceLocation);
        setServiceLocationError(serviceLocationValidationError);
        if (serviceLocationValidationError) isValid = false;

        const serviceInformationValidationError = validateServiceInformation(serviceInformation);
        setServiceInformationError(serviceInformationValidationError);
        if (serviceInformationValidationError) isValid = false;

        const livestreamLinkValidationError = validateLivestreamLink(liveStreamLink);
        setGraveLocationError(livestreamLinkValidationError);
        if (livestreamLinkValidationError) isValid = false;

        const fundraiserUlrValidationError = validateFundraiserUrl(liveStreamLink);
        setFundraiserUrlError(fundraiserUlrValidationError);
        if (fundraiserUlrValidationError) isValid = false;

        const graveLocationValidationError = validateGraveLocation(serviceLocation);
        setGraveLocationError(graveLocationValidationError);
        if (graveLocationValidationError) isValid = false;

        const plotInfoValidationError = validatePlotInfo(plotInfo);
        setPlotInfoError(plotInfoValidationError);
        if (plotInfoValidationError) isValid = false;

        const serviceMaterialValidationError = validateServiceMaterialType(serviceMaterialType);
        setServiceMaterialError(serviceMaterialValidationError);
        if (serviceMaterialValidationError) isValid = false;

        const obituaryValidationError = validateObituary(obituary);
        setObituaryError(obituaryValidationError);
        if (obituaryValidationError) isValid = false;

        if (isValid) {
            try {
                setIsSaving(true);
                setUploadProgress(0);
                setSubmitErrorMessage("");
                let createdId = "";

                if (mode === "add") {
                    if (isPetMemorial) {
                        changedInfoFields.isPetMemorial = true;
                    }
                    createdId = await createMemorial(changedInfoFields, progressCallback);
                    if (user?.userType === "standard_user") {
                        trackEvent("free_memorial_created", {
                            value: 0,
                            currency: "AUD",
                        });
                    }
                    void refreshUser();
                    const createdMemorial = await fetchMemorial(createdId);
                    setMemorial(createdMemorial);
                    setQrKey(createdMemorial.qrKey || "");
                    setIframeKey(Date.now());
                } else {
                    if (!id) {
                        throw new Error("No ID.");
                    }
                    if (hasUnsavedChanges) {
                        await updateMemorialInfo(id, changedInfoFields, progressCallback);
                    }
                }

                setChangedInfoFields({});
                setIsSaving(false);
                await delay(1); // Wait for saving state to be cleared
                if (mode === "add") {
                    // Change to edit mode after creation
                    searchParams.set("mode", "edit");
                    searchParams.set("id", createdId);
                    navigate(`?${searchParams.toString()}`, { replace: true });
                } else {
                    setIframeKey(Date.now()); // Update iframe key to refresh preview
                }
                return true;
            } catch (err) {
                const message = user?.userType === "standard_user"
                    ? "Failed to save memorial information.\nPlease ensure you have not exceeded the limit of 5 free version memorials and try again."
                    : "Failed to save memorial information. Please ensure payment information is correct.";
                setSubmitErrorMessage(message);
                alert(message);
            } finally {
                setIsSaving(false);
            }
        }
        return false;
    };

    const handleUpgrade = async () => {
        if (user?.userType === "root_admin") {
            setShowUpgradePopup(true);
            return;
        }
        try {
            const saveSuccess = await handleSave();
            if (saveSuccess) {
                void navigate(`/memorials/purchase-memorial?${new URLSearchParams({
                    memorialId: id || "",
                    type: isPetMemorial ? "pet" : "traditional",
                }).toString()}`);
            }
        } catch (err) {
            const msg = "Failed to upgrade memorial. Please try again.";
            setSubmitErrorMessage(msg);
            alert(msg);
        }
    };

    // Root admin only: saves, then upgrades without payment
    const handleAdminUpgrade = async () => {
        setShowUpgradePopup(false);
        const saveSuccess = await handleSave();
        if (!saveSuccess || !id) {
            return;
        }
        try {
            await upgradeMemorial(id);
            setIsFreeVersion(false);
            setSnackbar({ open: true, message: "Memorial upgraded", severity: "success" });
        } catch {
            setSnackbar({ open: true, message: "Failed to upgrade memorial.", severity: "error" });
        }
    };

    const baseMemorialUrl = memorial?.iframeUrl ? `${memorial.iframeUrl}?memorialId=${memorial.id}` : `${getRootDomain(memorial?.subdomain)}/memorials/${user?.userType === "demo" ? "example" : (memorial?.urlSlug || memorial?.id)}`;
    const shareUrl = memorial?.iframeUrl ? `${memorial.iframeUrl}?memorialId=${memorial.id}` : baseMemorialUrl;

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setSnackbar({
                open: true,
                message: "Link copied to clipboard",
                severity: "success",
            });
        } catch {
            setSnackbar({
                open: true,
                message: "Failed to copy link",
                severity: "error",
            });
        }
    };

    let header1 = "Forever";
    if (isPetMemorial) {
        header1 += " Pet";
    }
    header1 += " Memorial";
    if (mode === "add") {
        header1 += " Creator";
    } else {
        header1 += " Editor";
    }

    const renderContent = () => {
        if (loading || isSaving || error) {
            return (
                <LoadSaveErrorDisplay
                    isLoading={loading}
                    isSaving={isSaving}
                    progress={changedInfoFields.image ? uploadProgress : undefined}
                    error={error}
                />
            );
        }
        return (
            <>
                <div className={styles.headerContainer}>
                    <h1>{header1}</h1>
                </div>
                <div className={styles.buttonGroup}>
                    <EditorCompleteButtons
                        hasUnsavedChanges={hasUnsavedChanges}
                        isSaving={isSaving}
                        isNew={mode === "add"}
                        onSave={handleSave}
                    />
                    {(isFreeVersion && (mode === "edit")) && (
                        <Button variant="primary" onClick={handleUpgrade}>
                            Upgrade
                        </Button>
                    )}
                </div>
                <div
                    className={`${styles.editorLayout} ${
                        mode === "edit" && id ? "" : styles.centredLayout
                    }`}
                >
                    <div className={styles.formSection}>
                        <div className={styles.contentContainer}>
                            <h2>{user?.userType === "standard_user" ? "Let's Get Started" : "General"}</h2>
                            <Card className={styles.cardContainer}>
                                <MediaSelector
                                    label="Main Image"
                                    id="image"
                                    media={image}
                                    errorMessage={imageError}
                                    onMediaChange={(img, err) => {
                                        setImage(img as MediaFile);
                                        setImageError(err);
                                        setChangedInfoFields((prev) => ({ ...prev, image: img as MediaFile }));
                                    }}
                                    mediaTypeFilter="image"
                                />
                                <TextInput
                                    label={`${!isPetMemorial ? "First " : ""}Name *`}
                                    id="firstName"
                                    value={firstName}
                                    onChange={onFirstNameChange}
                                    placeholder="Enter first name..."
                                    errorMessage={firstNameError}
                                />
                                {!isPetMemorial && (
                                    <TextInput
                                        label={`Last Name${isPetMemorial ? "" : " *"}`}
                                        id="lastName"
                                        value={lastName}
                                        onChange={onLastNameChange}
                                        placeholder="Enter last name..."
                                        errorMessage={lastNameError}
                                    />
                                )}
                                {birthYearOnly ? (
                                    <TextInput
                                        label="Birth Year"
                                        id="birthYear"
                                        value={birthYear}
                                        onChange={onBirthYearChange}
                                        placeholder="Enter birth year..."
                                        errorMessage={birthYearError}
                                    />
                                ) : (
                                    <DateInput
                                        id="birthDate"
                                        label="Date of Birth"
                                        value={birthDate}
                                        onChange={onBirthDateChange}
                                        errorMessage={birthDateError}
                                    />
                                )}
                                {deathYearOnly ? (
                                    <TextInput
                                        label="Death Year"
                                        id="deathYear"
                                        value={deathYear}
                                        onChange={onDeathYearChange}
                                        placeholder="Enter death year..."
                                        errorMessage={deathYearError}
                                    />
                                ) : (
                                    <DateInput
                                        id="deathDate"
                                        label="Date of Death"
                                        value={deathDate}
                                        onChange={onDeathDateChange}
                                        errorMessage={deathDateError}
                                    />
                                )}
                                {isPetMemorial ? (
                                    <>
                                        <CheckboxWithLabel
                                            id="birthYearOnly"
                                            label="Birth Year Only"
                                            checked={birthYearOnly}
                                            onChange={onBirthYearOnlyChange}
                                        />
                                        <CheckboxWithLabel
                                            id="deathYearOnly"
                                            label="Death Year Only"
                                            checked={deathYearOnly}
                                            onChange={onDeathYearOnlyChange}
                                        />
                                    </>
                                ) : (
                                    <CheckboxWithLabel
                                        id="birthDeathYearOnly"
                                        label="Birth/Death Year Only"
                                        checked={birthYearOnly}
                                        onChange={(val) => {
                                            onBirthYearOnlyChange(val);
                                            onDeathYearOnlyChange(val);
                                        }}
                                    />
                                )}
                                {/* Not editable */}
                                {(user?.userType !== "standard_user") && pageAdminEmail && (
                                    <TextInput
                                        label="Page Admin Email"
                                        id="pageAdminEmail"
                                        value={pageAdminEmail}
                                        onChange={() => {}}
                                        disabled
                                    />
                                )}
                            </Card>
                        </div>
                        {isFreeVersion && mode === "edit" && (
                            <p className={styles.freeVersionNote}>
                                Note: All fields are optional.
                            </p>
                        )}
                        <p className={styles.freeVersionNoteMobile}>
                            View this page on a desktop or laptop to see a live preview of your memorial alongside the editor.
                        </p>
                        <div className={styles.lockedSection}>
                            {(isFreeVersion && (mode === "edit")) && (
                                <div className={styles.lockOverlay}>
                                    <div className={styles.lockMessage}>
                                        <p>Upgrade to complete their memorial</p>
                                        <Button variant="primary" onClick={handleUpgrade}>
                                            Upgrade
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {(!isFreeVersion || mode === "edit") && (
                                <>
                                    <div className={styles.contentContainer}>
                                        <h2>About</h2>
                                        <Card className={styles.cardContainer}>
                                            <TextInput
                                                label="Birthplace"
                                                id="birthplace"
                                                value={birthplace}
                                                onChange={onBirthplaceChange}
                                                placeholder="Enter birthplace..."
                                                errorMessage={birthplaceError}
                                            />
                                            {!isPetMemorial && (
                                                <TextInput
                                                    label="Residence"
                                                    id="residence"
                                                    value={residence}
                                                    onChange={onResidenceChange}
                                                    placeholder="Enter residence..."
                                                    errorMessage={residenceError}
                                                />
                                            )}
                                            <TextInput
                                                label="Grave Location (Including Suburb)"
                                                id="graveLocation"
                                                value={graveLocation}
                                                onChange={onGraveLocationChange}
                                                placeholder="Enter grave location..."
                                                errorMessage={graveLocationError}
                                            />
                                            {!isPetMemorial && (
                                                <TextInput
                                                    label="Plot Information"
                                                    id="plotInfo"
                                                    value={plotInfo}
                                                    onChange={onPlotInfoChange}
                                                    placeholder="Enter plot information..."
                                                    errorMessage={plotInfoError}
                                                />
                                            )}
                                            {businessHideSendFlowers ? (
                                                <div className={styles.note}>
                                                    <p>{`The Send Flowers button is hidden ${user?.userType === "standard_user" ? "by your funeral director." : "for all of your business's memorials and notices."}`}</p>
                                                </div>
                                            ) : (
                                                <CheckboxWithLabel
                                                    id="hideSendFlowers"
                                                    label="Hide Send Flowers Button"
                                                    checked={hideSendFlowers}
                                                    onChange={(val) => {
                                                        setHideSendFlowers(val);
                                                        setChangedInfoFields((prev) => {
                                                            const newState = { ...prev };
                                                            newState.hideSendFlowers = val;
                                                            return newState;
                                                        });
                                                    }}
                                                />
                                            )}
                                        </Card>
                                    </div>
                                    <div className={styles.contentContainer}>
                                        <h2>{isPetMemorial ? "Charity" : "Service"}</h2>
                                        <Card className={styles.cardContainer}>
                                            {!isPetMemorial && (
                                                <>
                                                    {!serviceId && (
                                                        <>
                                                            <DateInput
                                                                id="serviceDate"
                                                                label="Date of Service"
                                                                value={serviceDateTime}
                                                                onChange={onServiceDateChange}
                                                            />
                                                            <TimeInput
                                                                id="serviceTime"
                                                                label="Service Time"
                                                                disabled={!serviceDateTime}
                                                                value={
                                                                    serviceDateTime
                                                                        ? {
                                                                            hours: String(serviceDateTime.getUTCHours()).padStart(2, "0"),
                                                                            minutes: String(serviceDateTime.getUTCMinutes()).padStart(2, "0"),
                                                                        }
                                                                        : null
                                                                }
                                                                onChange={onServiceTimeChange}
                                                            />
                                                            <TextInput
                                                                label="Service Location"
                                                                description="Note: To ensure Google Maps location displays correctly, include full address"
                                                                value={serviceLocation}
                                                                onChange={onServiceLocationChange}
                                                                placeholder="Enter service location..."
                                                                id="serviceLocation"
                                                                errorMessage={serviceLocationError}
                                                            />
                                                            <TextInput
                                                                label="Service Information"
                                                                id="serviceInformation"
                                                                value={serviceInformation}
                                                                onChange={onServiceInformationChange}
                                                                placeholder="Enter additional information (eg. in lieu of flowers, livestream password, private event etc)..."
                                                                multiline
                                                                errorMessage={serviceInformationError}
                                                            />
                                                            {!businessId && !isPetMemorial && (
                                                                <TextInput
                                                                    label="Live Stream Link"
                                                                    id="liveStreamLink"
                                                                    value={liveStreamLink}
                                                                    onChange={onLiveStreamLinkChange}
                                                                    placeholder="Enter live stream url..."
                                                                    errorMessage={liveStreamLinkError}
                                                                />
                                                            )}
                                                        </>
                                                    )}
                                                    <FileSelector
                                                        label="Service Material (.pdf)"
                                                        id="service-book"
                                                        acceptedFileTypes=".pdf*"
                                                        maxSize={MAX_MEMORIAL_SERVICE_BOOK_FILE_SIZE}
                                                        errorMessage={serviceMaterialError}
                                                        onFileChange={(file, err) => {
                                                            setServiceMaterialError(err);
                                                            setServiceMaterial(file);
                                                            setChangedInfoFields((prev) => {
                                                                const newState = { ...prev };
                                                                newState.serviceMaterial = file;
                                                                return newState;
                                                            });
                                                        }}
                                                    />
                                                    <SelectInput
                                                        label="Service Material Type"
                                                        id="serviceMaterialType"
                                                        value={serviceMaterialType || ""}
                                                        disabled={!serviceMaterial}
                                                        onChange={(val) => {
                                                            setServiceMaterialType(val as ServiceMaterialType);

                                                            setChangedInfoFields((prev) => {
                                                                const newState = { ...prev };
                                                                newState.serviceMaterialType = val as ServiceMaterialType;
                                                                return newState;
                                                            });
                                                        }}
                                                        options={SERVICE_MATERIAL_OPTIONS}
                                                        errorMessage={serviceMaterialTypeError}
                                                    />
                                                </>
                                            )}
                                            <SelectInput
                                                id="charityId"
                                                label="Charity/Fundraiser"
                                                value={charityId}
                                                onChange={(val) => {
                                                    setCharityId(val);
                                                    setChangedInfoFields((prev) => ({
                                                        ...prev,
                                                        charityId: val === "custom" ? "" : val,
                                                        fundraiserUrl: val !== "custom" ? "" : prev.fundraiserUrl,
                                                    }));
                                                }}
                                                options={[
                                                    { label: "***Custom Fundraiser Link***", value: "custom" },
                                                    ...charities
                                                        .filter((char) => (char.status === "active" || char.id === charityId)
                                                            && (!isPetMemorial || char.isPetCharity))
                                                        .map((char) => ({
                                                            label: char.name,
                                                            value: char.id,
                                                        }))
                                                        .sort((a, b) => a.label.localeCompare(b.label)),
                                                ]}
                                            />
                                            {(charityId === "custom") && (
                                                <TextInput
                                                    label="Fundraiser Link"
                                                    id="fundraiserUrl"
                                                    value={fundraiserUrl}
                                                    onChange={onFundraiserUrlChange}
                                                    placeholder="Enter fundraiser url..."
                                                    errorMessage={fundraiserUrlError}
                                                />
                                            )}
                                            <div className={styles.note}>
                                                <p>If there is a specific charity you would like for your memorial that is not listed, please contact us.</p>
                                            </div>
                                            {guestBookAllowed && (
                                                <CheckboxWithLabel
                                                    id="guestBookOpen"
                                                    label="Allow New Guest Book Signatures"
                                                    description="Recommend disabling after service complete"
                                                    checked={privacy.guestBookOpen}
                                                    onChange={(val) => {
                                                        setPrivacy((prevState) => ({
                                                            ...prevState,
                                                            guestBookOpen: val,
                                                        }));
                                                        setChangedInfoFields((prev) => {
                                                            const newState = { ...prev };
                                                            newState.privacy = {
                                                                ...privacy,
                                                                guestBookOpen: val,
                                                            };
                                                            return newState;
                                                        });
                                                    }}
                                                />
                                            )}
                                            {serviceId && (
                                                <div className={styles.note}>
                                                    <p>{`Other service information is managed by ${user?.userType === "standard_user" ? "your funeral director." : "the linked service."}`}</p>
                                                </div>
                                            )}
                                        </Card>
                                    </div>
                                    <div className={styles.contentContainer}>
                                        <h2>Obituary</h2>
                                        <Card className={styles.cardContainer}>
                                            <TextInput
                                                label="Obituary"
                                                id="obituary"
                                                value={obituary}
                                                onChange={onObituaryChange}
                                                placeholder="Enter obituary..."
                                                multiline
                                                errorMessage={obituaryError}
                                            />
                                        </Card>
                                    </div>
                                    <div className={styles.contentContainer}>
                                        <h2>Life Timeline</h2>
                                        <Button to={`../timeline-editor?${new URLSearchParams({ id: id || "" }).toString()}`} disabled={mode === "add"}>
                                            Edit Timeline
                                        </Button>
                                        <p className={styles.description2}>
                                            Create memorial to enable timeline editing
                                        </p>
                                    </div>
                                    <div className={styles.contentContainer}>
                                        <h2>Gallery</h2>
                                        <Button to={`../gallery-editor?${new URLSearchParams({ id: id || "" }).toString()}`} disabled={mode === "add"}>
                                            Edit Gallery
                                        </Button>
                                        <p className={styles.description2}>
                                            Create memorial to enable gallery editing
                                        </p>
                                    </div>
                                    <div className={styles.contentContainer}>
                                        <h2>Privacy</h2>
                                        <Card className={styles.cardContainer}>
                                            <CheckboxWithLabel
                                                id="indexable"
                                                label="Appear In Search"
                                                description="Viewable in Memorial List and Google Search"
                                                checked={!privacy.noIndex}
                                                onChange={(val) => {
                                                    setPrivacy((prevState) => ({
                                                        ...prevState,
                                                        noIndex: !val,
                                                    }));
                                                    setChangedInfoFields((prev) => {
                                                        const newState = { ...prev };
                                                        newState.privacy = {
                                                            ...privacy,
                                                            noIndex: !val,
                                                        };
                                                        return newState;
                                                    });
                                                }}
                                            />
                                            <CheckboxWithLabel
                                                id="tributesOpen"
                                                label="Allow New Tributes"
                                                checked={privacy.tributesOpen}
                                                onChange={(val) => {
                                                    setPrivacy((prevState) => ({
                                                        ...prevState,
                                                        tributesOpen: val,
                                                    }));
                                                    setChangedInfoFields((prev) => {
                                                        const newState = { ...prev };
                                                        newState.privacy = {
                                                            ...privacy,
                                                            tributesOpen: val,
                                                        };
                                                        return newState;
                                                    });
                                                }}
                                            />
                                            {(user?.userType === "standard_user") && (
                                                <CheckboxWithLabel
                                                    id="emailOnTribute"
                                                    label="Receive Email on New Tribute"
                                                    checked={privacy.emailOnTribute}
                                                    onChange={(val) => {
                                                        setPrivacy((prevState) => ({
                                                            ...prevState,
                                                            emailOnTribute: val,
                                                        }));
                                                        setChangedInfoFields((prev) => {
                                                            const newState = { ...prev };
                                                            newState.privacy = {
                                                                ...privacy,
                                                                emailOnTribute: val,
                                                            };
                                                            return newState;
                                                        });
                                                    }}
                                                />
                                            )}
                                            <h4>QR Code Restrictions</h4>
                                            <p className={styles.description}>
                                                Enable the following to restrict feature access to QR code only
                                            </p>
                                            <CheckboxWithLabel
                                                id="restrictPostContentToQr"
                                                label={`Post ${guestBookAllowed ? "Content" : "Tributes"}`}
                                                description={guestBookAllowed ? "Tributes and Guest Book" : ""}
                                                checked={privacy.restrictPostContentToQr}
                                                onChange={(val) => {
                                                    setPrivacy((prevState) => ({
                                                        ...prevState,
                                                        restrictPostContentToQr: val,
                                                    }));
                                                    setChangedInfoFields((prev) => {
                                                        const newState = { ...prev };
                                                        newState.privacy = {
                                                            ...privacy,
                                                            restrictPostContentToQr: val,
                                                        };
                                                        return newState;
                                                    });
                                                }}
                                            />
                                            <CheckboxWithLabel
                                                id="restrictViewInfoToQr"
                                                label={`View About${isPetMemorial ? ", Service" : ""}, Obituary`}
                                                checked={privacy.restrictViewInfoToQr}
                                                onChange={(val) => {
                                                    setPrivacy((prevState) => ({
                                                        ...prevState,
                                                        restrictViewInfoToQr: val,
                                                    }));
                                                    setChangedInfoFields((prev) => {
                                                        const newState = { ...prev };
                                                        newState.privacy = {
                                                            ...privacy,
                                                            restrictViewInfoToQr: val,
                                                        };
                                                        return newState;
                                                    });
                                                }}
                                            />
                                            <CheckboxWithLabel
                                                id="restrictViewTimelineGalleryToQr"
                                                label="View Gallery & Timeline"
                                                checked={privacy.restrictViewTimelineGalleryToQr}
                                                onChange={(val) => {
                                                    setPrivacy((prevState) => ({
                                                        ...prevState,
                                                        restrictViewTimelineGalleryToQr: val,
                                                    }));
                                                    setChangedInfoFields((prev) => {
                                                        const newState = { ...prev };
                                                        newState.privacy = {
                                                            ...privacy,
                                                            restrictViewTimelineGalleryToQr: val,
                                                        };
                                                        return newState;
                                                    });
                                                }}
                                            />
                                            <CheckboxWithLabel
                                                id="restrictViewTributesToQr"
                                                label="View Tributes"
                                                checked={privacy.restrictViewTributesToQr}
                                                onChange={(val) => {
                                                    setPrivacy((prevState) => ({
                                                        ...prevState,
                                                        restrictViewTributesToQr: val,
                                                    }));
                                                    setChangedInfoFields((prev) => {
                                                        const newState = { ...prev };
                                                        newState.privacy = {
                                                            ...privacy,
                                                            restrictViewTributesToQr: val,
                                                        };
                                                        return newState;
                                                    });
                                                }}
                                            />
                                        </Card>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    {mode === "edit" && id && (
                        <div className={styles.previewSection}>
                            <div className={styles.previewHeader}>
                                {isFreeVersion ? (
                                    <h2><AutoAwesomeIcon fontSize="medium" /> Enhanced Preview</h2>
                                ) : (
                                    <h2>Preview</h2>
                                )}
                                <div className={styles.previewActions}>
                                    <Button variant="text" onClick={handleCopyLink}>
                                        <ContentCopyIcon style={{ marginRight: "3px" }} fontSize="inherit" />
                                        Copy Link
                                    </Button>
                                    <Button variant="text" href={shareUrl} target="_blank">
                                        View
                                        <OpenInNewIcon style={{ marginLeft: "3px" }} fontSize="inherit" />
                                    </Button>
                                </div>
                            </div>
                            <div className={styles.previewContainer}>
                                <>
                                    <iframe
                                        key={iframeKey}
                                        title="Preview Frame"
                                        src={`${baseMemorialUrl}?qrKey=${qrKey}&preview=${isFreeVersion ? "true" : "false"}`}
                                        className={styles.previewFrame}
                                    />
                                    {hasUnsavedChanges && (
                                        <div className={styles.previewOverlay}>
                                            <p>Save changes to refresh the preview</p>
                                        </div>
                                    )}
                                </>
                            </div>
                        </div>
                    )}
                </div>
                {submitErrorMessage && (
                    <p className={styles.errorMessage}>{submitErrorMessage}</p>
                )}
                <UnsavedChangesPrompt hasUnsavedChanges={hasUnsavedChanges} />

                <Snackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                />
            </>
        );
    };

    return (
        <div className={styles.container}>
            {renderContent()}
            <ConfirmationPopup
                visible={showUpgradePopup}
                message={`Upgrade ${firstName} ${lastName}'s memorial to the full version without payment? Unsaved changes will be saved first.`}
                confirmButtonText="Upgrade"
                variant="primary"
                onConfirm={handleAdminUpgrade}
                onCancel={() => setShowUpgradePopup(false)}
            />
        </div>
    );
};

export default MemorialEditorPage;
